import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';

import { useMappingDashboard } from '../lib/use-mapping-dashboard';
import type { useMappingFilters } from '../lib/use-mapping-filters';
import { MapContainer } from './components';

interface MappingPageProps {
  /** Owned by the wrapper, which also publishes them to the header panel. */
  filters: ReturnType<typeof useMappingFilters>;
}

export function MappingPage({ filters }: MappingPageProps) {
  const { t } = useTranslation();

  const dashboard = useMappingDashboard({
    organizationId: filters.selectedOrganization,
    vehicleCategories: filters.selectedVehicleCategories,
    consignmentNumber: filters.debouncedConsignmentNumber
  });

  if (dashboard.error) {
    return (
      <Card className="p-4 md:p-6">
        <p className="text-destructive">{t('welcome.map.error.message')}</p>
      </Card>
    );
  }

  if (dashboard.isLoading && dashboard.locations.length === 0) {
    return (
      <Card className="p-4 md:p-6">
        <p className="text-center text-muted-foreground">{t('welcome.map.loading.message')}</p>
      </Card>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:gap-6">
      <Card className="flex min-h-0 flex-1 flex-col p-4">
        <MapContainer
          clusters={dashboard.clusters}
          iconLocations={dashboard.iconLocations}
          fitPoints={dashboard.locations}
          corridors={dashboard.corridors}
          dataFilters={dashboard.dataFilters}
          activeLayers={dashboard.layers.activeLayers}
          onLayerToggle={dashboard.layers.toggleLayer}
          groupBy={dashboard.groupBy}
          onGroupByChange={dashboard.setGroupBy}
          timeMachine={dashboard.timeMachine}
          weeksAgo={dashboard.weeksAgo}
          showTimeMachine={dashboard.showTimeMachine}
          onToggleTimeMachine={() => dashboard.setShowTimeMachine(!dashboard.showTimeMachine)}
          isLoading={dashboard.isDataFetching}
        />
      </Card>
    </div>
  );
}
